import { createReducer, on } from '@ngrx/store';

import { initialTemplateState } from './groupTemplate.state';
import { GroupTemplateActions } from './groupTemplate.actions';
import { GroupReportsTemplateModel } from './groupTemplate.model';

const createEmptyTemplate = (): GroupReportsTemplateModel => ({
  idobject: 0,
  templateName: '',
  templateDescription: '',
  surveyVersionOid: 0,
  created: '',
  createdBy: '',
  lastUsed: null,
  filter: {
    target: {
      associations: [],
      organizations: [],
      distributions: [],
      classRanks: [],
      minAge: null,
      maxAge: null,
      startDate: '',
      endDate: '',
    },
    benchmark: {
      associations: [],
      organizations: [],
      distributions: [],
      classRanks: [],
      minAge: null,
      maxAge: null,
      startDate: '',
      endDate: '',
    },
  },
  templateConfig: {
    performanceSettings: {
      averageScoreBreakpoint: 40,
      averageScoreDisplay: 'Average',
      highScoreBreakpoint: 60,
      highScoreDisplay: 'High',
      lowScoreDisplay: 'Low',
    },
    contentSettings: {
      useOpenEnded: false,
      useDemographics: false,
      useReportSummary: true,
    },
    likertScaleSettings: {
      useDomainsLevel: true,
      useScalesLevel: true,
      useItemsLevel: true,
    },
    reportTitle: '',
    reportSubTitle: '',
    introductoryText: '',
    useIntroductoryText: false,
    usePercentileCalc: false,
    GMOoYR: {
      useGMOoYR: false,
      title: '',
      guidanceContent: '',
    },
    summarySettings: {
      performanceSettings: {
        averageScoreBreakpoint: 40,
        highScoreBreakpoint: 60,
      },
      feedbackSettings: {
        lowText: '',
        averageText: '',
        highText: '',
      },
      actionSettings: {
        lowText: '',
        averageText: '',
        highText: '',
      },
      displaySettings: {
        useLikertScale: true,
        useTargetPercentage: true,
        useBenchmarkPercentage: true,
        usePercentile: false,
        useFeedback: true,
        useActions: true,
      },
      comparisonText: '',
    },
    domainsSettings: [],
  },
});

export const groupTemplateReducer = createReducer(
  initialTemplateState,

  // reset store to initial value
  on(GroupTemplateActions.resetStore, () => initialTemplateState),

  on(GroupTemplateActions.initNewTemplate, (state) => ({
    ...state,
    loading: false,
    error: null,
    isNew: true,
    selectedTemplate: 0,
    template: createEmptyTemplate(),
    templateValidation: {
      surveyInvalid: false,
      templateNameInvalid: false,
    },
  })),

  on(GroupTemplateActions.loadTemplate, (state) => ({
    ...state,
    loading: true,
    error: null,
    isNew: false,
    templateValidation: {
      surveyInvalid: false,
      templateNameInvalid: false,
    },
  })),

  on(GroupTemplateActions.loadTemplateSuccess, (state, { template }) => ({
    ...state,
    loading: false,
    isNew: false,
    template,
    selectedTemplate: template?.idobject || 0,
    templateValidation: {
      surveyInvalid: false,
      templateNameInvalid: false,
    },
  })),

  on(GroupTemplateActions.loadTemplateFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    templateValidation: {
      surveyInvalid: false,
      templateNameInvalid: false,
    },
  })),

  on(GroupTemplateActions.updateTemplateInformation, (state, { templateName, templateDescription }) => ({
    ...state,
    template: state.template
      ? {
          ...state.template,
          templateName,
          templateDescription,
        }
      : null,
    templateValidation: {
      ...state.templateValidation,
      templateNameInvalid: false,
    },
  })),

  on(GroupTemplateActions.updateSelectedSurvey, (state, { surveyVersionOid }) => ({
    ...state,
    template: state.template
      ? {
          ...state.template,
          surveyVersionOid,
        }
      : null,
    templateValidation: {
      ...state.templateValidation,
      surveyInvalid: false,
    },
  })),

  on(GroupTemplateActions.updateTemplateConfig, (state, { templateConfig }) => ({
    ...state,
    template: state.template
      ? {
          ...state.template,
          templateConfig: {
            ...state.template.templateConfig,
            ...templateConfig,
          },
        }
      : null,
  })),
  on(GroupTemplateActions.updateTemplateGMOoYR, (state, { templateGMOoYR }) => ({
    ...state,
    template: state.template
      ? {
          ...state.template,
          templateConfig: {
            ...state.template.templateConfig,
            GMOoYR: {
              ...state.template.templateConfig.GMOoYR,
              ...templateGMOoYR,
            },
          },
        }
      : null,
  })),
  on(GroupTemplateActions.updateContentSettings, (state, { contentSettings }) => ({
    ...state,
    template: state.template
      ? {
          ...state.template,
          templateConfig: {
            ...state.template.templateConfig,
            contentSettings: {
              ...state.template.templateConfig.contentSettings,
              ...contentSettings,
            },
          },
        }
      : null,
  })),
  on(GroupTemplateActions.updateLikertScaleSettings, (state, { likertScaleSettings }) => ({
    ...state,
    template: state.template
      ? {
          ...state.template,
          templateConfig: {
            ...state.template.templateConfig,
            likertScaleSettings: {
              ...state.template.templateConfig.likertScaleSettings,
              ...likertScaleSettings,
            },
          },
        }
      : null,
  })),

  on(GroupTemplateActions.updatePerformanceSettings, (state, { performanceSettings }) => ({
    ...state,
    template: state.template
      ? {
          ...state.template,
          templateConfig: {
            ...state.template.templateConfig,
            performanceSettings: {
              ...state.template.templateConfig.performanceSettings,
              ...performanceSettings,
            },
          },
        }
      : null,
  })),
  on(GroupTemplateActions.updateDomainSettings, (state, { domainsSettings }) => ({
    ...state,
    template: state.template
      ? {
          ...state.template,
          templateConfig: {
            ...state.template.templateConfig,
            domainsSettings,
          },
        }
      : null,
  })),
  on(GroupTemplateActions.updateFilterTarget, (state, { filterTarget }) => ({
    ...state,
    template: state.template
      ? {
          ...state.template,
          filter: {
            ...state.template.filter,
            target: {
              ...state.template.filter.target,
              ...filterTarget,
            },
          },
        }
      : null,
  })),
  on(GroupTemplateActions.updateFilterBenchmark, (state, { filterBenchmark }) => ({
    ...state,
    template: state.template
      ? {
          ...state.template,
          filter: {
            ...state.template.filter,
            benchmark: {
              ...state.template.filter.benchmark,
              ...filterBenchmark,
            },
          },
        }
      : null,
  })),
  on(GroupTemplateActions.setTemplateValidation, (state, { surveyInvalid, templateNameInvalid }) => ({
    ...state,
    templateValidation: {
      surveyInvalid,
      templateNameInvalid,
    },
  })),
);
